/**
 * A WeakMap2 is an extension of the built-in WeakMap class that
 * provides additional methods for working with weakly held key-value pairs.
 */
export class WeakMap2<K extends WeakKey, V> extends WeakMap<K, V> {
    /**
     * The getOrInsert() method of WeakMap instances returns the value corresponding to the
     * specified key in this WeakMap. If the key is not present, it inserts a new entry
     * with the key and a given default value, and returns the inserted value.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakMap/getOrInsert
     * @note
     *   This method is part of the ECMAScript 2026 standard but not supported in all JavaScript environments yet.
     *   Adding it here, so we need no global polyfills and can use it in our codebase.
     *
     * @param key The key of the value to return from the WeakMap object.
     *   Must be either an object or a non-registered symbol.
     *   Object keys are compared by reference, not by value.
     * @param defaultValue The value to insert and return if the key is not already present in the WeakMap object.
     * @returns The value associated with the specified key in the WeakMap object.
     *   If the key can't be found, defaultValue is inserted and returned.
     */
    getOrInsert(key: K, defaultValue: V): V {
        if (this.has(key)) return this.get(key)!;
        this.set(key, defaultValue);
        return defaultValue;
    }
    /**
     * The getOrInsertComputed() method of WeakMap instances returns the
     * value corresponding to the specified key in this WeakMap.
     * If the key is not present, it inserts a new entry with the key
     * and a default value computed from a given callback, and returns the inserted value.
     *
     * Use this method instead of `getOrInsert()` when the default value is
     * expensive to compute, and you want to avoid computing it unless it's actually needed.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakMap/getOrInsertComputed
     * @note
     *   This method is part of the ECMAScript 2026 standard but not supported in all JavaScript environments yet.
     *   Adding it here, so we need no global polyfills and can use it in our codebase.
     *
     * @param key The key of the element to return from the Map object.
     *   Must be either an object or a non-registered symbol.
     *   Object keys are compared by reference, not by value.
     * @param factory A function that returns the value to insert and
     *   return if the key is not already present in the Map object.
     *   The function is called with with key as its only argument.
     * @returns The value associated with the specified key in the WeakMap object.
     *   If the key can't be found, the result of callback(key) is inserted and returned.
     */
    getOrInsertComputed(key: K, factory: (key: K) => V): V {
        if (this.has(key)) return this.get(key)!;
        const value = factory(key);
        this.set(key, value);
        return value;
    }
}
